import React, { useState } from 'react';
import classnames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom-v5-compat';

import { Button } from 'ui-components/components/button';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { NotificationService, NOTIFICATION_TYPE_SUCCESS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { PurchaseOrderCloseModal } from './purchase-order-close-modal';
import { messages } from './purchase-order-header.messages';
import styles from './purchase-order-header.styles.module.less';
import { PurchaseOrderPdfDropdown } from './purchase-order-pdf-dropdown';
import { ConfirmDialogueModal } from '../../../../components/modals/confirm-dialogue-modal';
import { useEnableSendPurchaseOrderEmails } from '../../../../feature-flags/hooks/use-enable-send-purchase-order-emails';
import { useModal } from '../../../../hooks/use-modal';
import { BASE_PRODUCTS_PATH, PURCHASE_ORDERS_PATH } from '../../../../routes/constants';
import { emailApi } from '../api/email-api';
import { messages as modalMessages } from '../common/messages';
import { PurchaseOrderStatus } from '../components/purchase-order-status/purchase-order-status';
import { SendEmailConfirmationModal } from '../components/send-email-confirmation-modal/send-email-confirmation-modal';
import {
  canDuplicatePurchaseOrder,
  canReceivePurchaseOrder,
  canCancelPurchaseOrder,
  canClosePurchaseOrder,
  canEditPurchaseOrder,
  canPrintPurchaseOrder,
  canSendOrderEmail,
  canSendReceivedEmail,
} from '../data/hooks/usePurchaseOrderPermissions';
import { purchaseOrderEmailTypes } from '../data/purchase-order-email-types';
import { purchaseOrderStatuses, purchaseOrderStatusNumbers } from '../data/purchase-order-statuses';
import { usePurchaseOrderContext } from '../purchase-order-context';
import { PurchaseOrdersBaseRoute } from '../purchase-orders-routes';
import { ReceivePurchaseOrderButton } from '../receive-purchase-order-button';

/**
 * Purchase Order Header component
 * Displays the header of the purchase order page with the order number, status, and actions
 * Uses centralized permission logic from usePurchaseOrderPermissions hook
 * @param {object} props - Component props
 * @param {() => void} props.toggleIsEditable - Toggle edit mode
 * @param {boolean} props.isEditMode - Flag to indicate if the page is in edit mode
 * @param {string} props.purchaseOrderId
 * @param {() => void} props.refreshCallback
 * @returns {JSX.Element} The header component
 */
export const PurchaseOrderHeader = ({ toggleIsEditable, purchaseOrderId, isEditMode, refreshCallback }) => {
  const {
    purchaseOrder,
    onUpdateStatus,
    updateOrder,
    duplicateThePurchaseOrder,
    fetchPurchaseOrder,
    resetPurchaseOrder,
    hasChanged,
    refreshPurchaseOrders,
  } = usePurchaseOrderContext();

  const { open: openCancelModal, close: closeCancelConfirmationModal, isOpen: isCancelConfirmationModalOpen } = useModal();

  const { open: openConfirmationModal, close: closeConfirmationModal, isOpen: isConfirmationModalOpen } = useModal();

  const {
    open: openSendOrderEmailConfirmationModal,
    close: closeSendOrderEmailConfirmationModal,
    isOpen: isSendOrderEmailConfirmationModalOpen,
  } = useModal();

  const intl = useIntl();
  const navigate = useNavigate();
  const { status } = purchaseOrder;
  const isSendingEmailsEnabled = useEnableSendPurchaseOrderEmails();

  const updatePreview = () => {
    refreshCallback();
    fetchPurchaseOrder();
    if (refreshPurchaseOrders) refreshPurchaseOrders();
  };

  const [isActionsLoading, setIsActionsLoading] = useState(false);

  const handleDuplicateOrder = async () => {
    setIsActionsLoading(true);
    const result = await duplicateThePurchaseOrder();
    if (result) {
      setIsActionsLoading(false);
      navigate(BASE_PRODUCTS_PATH + PURCHASE_ORDERS_PATH);
    }
  };

  const closeOrderHandler = async () => {
    setIsActionsLoading(true);

    const result = await onUpdateStatus(purchaseOrderStatusNumbers.closed);
    fetchPurchaseOrder();
    if (result) {
      NotificationService.showNotification({
        title: <FormattedMessage {...messages.closeSuccessMessage} />,
        notificationType: NOTIFICATION_TYPE_SUCCESS,
      });
      setIsActionsLoading(false);
    }
  };

  const cancelOrderHandler = async () => {
    setIsActionsLoading(true);

    const result = await onUpdateStatus(purchaseOrderStatusNumbers.cancelled);
    fetchPurchaseOrder();
    if (result) {
      NotificationService.showNotification({
        title: <FormattedMessage {...messages.cancelSuccessMessage} />,
        notificationType: NOTIFICATION_TYPE_SUCCESS,
      });
      setIsActionsLoading(false);
    }
  };

  const handleSave = async () => {
    const valid = await updateOrder();
    if (valid) {
      navigate(PurchaseOrdersBaseRoute);
    }
  };

  const handleCancelEdit = () => {
    if (hasChanged) {
      openCancelModal();
      return;
    }

    toggleIsEditable(false);
  };

  const handleCancelModalConfirmation = () => {
    resetPurchaseOrder();
    closeCancelConfirmationModal();
    toggleIsEditable(false);
  };

  const handleSendOrderEmailConfirmation = async (customMessage, customSubject, ccEmails) => {
    try {
      setIsActionsLoading(true);
      await emailApi.sendEmail(
        purchaseOrder.purchaseOrderId,
        purchaseOrder.status === purchaseOrderStatuses.open ? purchaseOrderEmailTypes.ordered : purchaseOrderEmailTypes.received,
        customMessage,
        customSubject,
        ccEmails,
      );

      NotificationService.showNotification({
        title: <FormattedMessage {...messages.emailPoSuccess} />,
        notificationType: NOTIFICATION_TYPE_SUCCESS,
      });
      closeSendOrderEmailConfirmationModal();
    } catch (error) {
      const contentMessage =
        error?.response?.status === 400 ? (
          <FormattedMessage {...messages.emailPoFailureBadRequestContent} />
        ) : (
          <FormattedMessage {...messages.emailPoFailureContent} />
        );

      NotificationService.showNotification({
        title: <FormattedMessage {...messages.emailPoFailureTitle} />,
        content: contentMessage,
        notificationType: NOTIFICATION_TYPE_ERROR,
      });
    } finally {
      setIsActionsLoading(false);
    }
  };

  return (
    <div className={styles.header}>
      <div className={styles['title-container']}>
        <Text
          className={styles.title}
          textType={TextType.H1}
          value={intl.formatMessage(messages.orderNumberHeader, {
            orderId: purchaseOrder.orderNumber,
          })}
        />
        <div className={styles['status-wrapper']}>
          <PurchaseOrderStatus status={status} />
        </div>
      </div>
      <div className={styles['buttons-container']}>
        {isEditMode ? (
          <>
            <Button className={styles['cancel-button']} onClick={handleCancelEdit} theme={THEME_CONSTANTS.tertiary}>
              <FormattedMessage {...messages.cancel} />
            </Button>
            <Button className={styles['save-button']} disabled={!hasChanged} onClick={handleSave} theme={THEME_CONSTANTS.primary}>
              <FormattedMessage {...messages.save} />
            </Button>
          </>
        ) : null}
        {isActionsLoading ? (
          <div className={styles['button-placeholder']}>
            {' '}
            <LoadingIndicator />{' '}
          </div>
        ) : (
          <DropdownMenu
            className={classnames(styles['menu-dropdown'], { [styles['menu-dropdown-disabled']]: isEditMode })}
            disabled={isEditMode}
            menuPosition='bottom-left'
          >
            <DropdownMenuToggler
              className={classnames(styles['actions-button'], { [styles['actions-button-disabled']]: isEditMode })}
              disabled={isEditMode}
            >
              <Text className={styles['actions-text']} textType={TextType.Label} value={messages.actionsDropdown} />
            </DropdownMenuToggler>
            {canDuplicatePurchaseOrder(status) && (
              <DropdownMenuItem onClick={handleDuplicateOrder}>
                <FormattedMessage {...messages.actionsDuplicate} />
              </DropdownMenuItem>
            )}
            {canCancelPurchaseOrder(status) && (
              <DropdownMenuItem onClick={openConfirmationModal}>
                <FormattedMessage {...messages.actionsCancelOrder} />
              </DropdownMenuItem>
            )}
            {canEditPurchaseOrder(status) && !isEditMode && (
              <DropdownMenuItem onClick={() => toggleIsEditable(!isEditMode)}>
                <FormattedMessage {...messages.actionsEdit} />
              </DropdownMenuItem>
            )}
            {canSendOrderEmail(status) && isSendingEmailsEnabled ? (
              <DropdownMenuItem onClick={openSendOrderEmailConfirmationModal}>
                <FormattedMessage {...messages.actionsSendOrderEmail} />
              </DropdownMenuItem>
            ) : null}
            {canSendReceivedEmail(status) && isSendingEmailsEnabled ? (
              <DropdownMenuItem onClick={openSendOrderEmailConfirmationModal}>
                <FormattedMessage {...messages.actionsSendOrderEmail} />
              </DropdownMenuItem>
            ) : null}
          </DropdownMenu>
        )}
        {canPrintPurchaseOrder(status) && (
          <PurchaseOrderPdfDropdown data={[purchaseOrder]} disabled={isEditMode} purchaseOrderIds={[purchaseOrderId]} />
        )}
        {canReceivePurchaseOrder(status) && (
          <ReceivePurchaseOrderButton
            buttonMessage={messages.receiveStock}
            disabled={isEditMode}
            purchaseOrder={purchaseOrder}
            refreshCallback={updatePreview}
            theme={THEME_CONSTANTS.primary}
          />
        )}
        {canClosePurchaseOrder(status) && (
          <Button data-testid='receive-purchase-order-button' onClick={openConfirmationModal} theme={THEME_CONSTANTS.primary}>
            <FormattedMessage {...messages.actionsCloseOrder} />
          </Button>
        )}
        {isConfirmationModalOpen ? (
          <PurchaseOrderCloseModal
            close={closeConfirmationModal}
            isCloseModal={canClosePurchaseOrder(status)}
            purchaseOrderNumber={purchaseOrder.orderNumber}
            save={canClosePurchaseOrder(status) ? closeOrderHandler : cancelOrderHandler}
          />
        ) : null}

        {isCancelConfirmationModalOpen ? (
          <ConfirmDialogueModal
            body={<FormattedMessage {...modalMessages.confirmCancelBody} />}
            buttonMessage={<FormattedMessage {...modalMessages.confirmCancelButton} />}
            header={<FormattedMessage {...modalMessages.confirmCancelHeader} />}
            onClose={closeCancelConfirmationModal}
            onConfirm={handleCancelModalConfirmation}
          />
        ) : null}
        {isSendOrderEmailConfirmationModalOpen ? (
          <SendEmailConfirmationModal
            isLoading={isActionsLoading}
            onClose={closeSendOrderEmailConfirmationModal}
            onConfirm={handleSendOrderEmailConfirmation}
          />
        ) : null}
      </div>
    </div>
  );
};
